import { Alert, Box, DialogContent, Divider, Step, StepContent, StepLabel, Stepper, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import IndieBackButton from 'components/forms/IndieBackButton';
import IndieNextStepButton from 'components/forms/IndieNextStepButton';
import IndieSaveButton from 'components/forms/IndieSaveButton';
import SelectOrCreateClientStep from 'components/forms/communities/SelectOrCreateClientStep';
import { CommunityContext } from 'contexts/communities/CommunityContext';
import { UserFeedbackContext } from 'contexts/UserFeedbackContext';
import { Form, Formik, useFormikContext } from 'formik';
import ApplicationList from 'page-sections/common/ApplicationList';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import * as Yup from 'yup';
import { IndieLabelAndValue } from './NewCommunityDialog';

const ConfigureCommunityMemberDialogContent = ({ open, onClose, onSuccess, installation = null }) => {

    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(0);
    const [steps, setSteps] = useState([]);
    const [validationSchemas, setValidationSchemas] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const { community, configureMember } = useContext(CommunityContext);
    const { setAndShowErrorMessage } = useContext(UserFeedbackContext);
    const { communityId } = useParams();

    const initialValues = {
        installation_id: installation?.id,
        installation: installation,
        community_id: communityId,
        client: null,
        client_id: null,
    };

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleSubmit = (values) => {
        if (activeStep < steps.length - 1) {
            handleNext();
        } else {
            setSubmitting(true);
            configureMember(values.installation_id, values.client_id).then((response) => {
                if (onSuccess) {
                    onSuccess(response.data);
                } else {
                    onClose();
                }
            }).catch((e) => {
                console.error(e);
                setAndShowErrorMessage(e);
            }).finally(() => {
                setSubmitting(false);
            });
        }
    };

    if (!community && open) {
        console.error("community is required in NewCommunityMemberDialog");
        setAndShowErrorMessage("error.api.generic");
        onClose();
    }

    useEffect(() => {
        const _steps = [];
        const _validationSchemas = [];

        // Always shown, even when the installation already arrives pre-selected from another
        // screen (e.g. the "Novo Membro"/"Configurar Nova Instalação" flows) — in that case it
        // just shows an Alert with the installation's data instead of the selection table,
        // mirroring the initialCommunity/initialMember pattern used elsewhere in these wizards.
        _steps.push({
            label: t("label.step.selectInstallation"),
            component: <SelectInstallationStep initialInstallation={installation} />
        });

        _validationSchemas.push(Yup.object().shape({
            installation_id: Yup.number().required(t("error.forms.mandatory"))
        }));

        _steps.push({
            label: t("label.step.selectClient"),
            component: <SelectOrCreateClientStep community={community} />
        });

        _validationSchemas.push(
            Yup.object().shape({
                client_id: Yup.number().required(t("error.forms.mandatory")),
            })
        );

        _steps.push({
            label: t("label.step.confirm"),
            component: <ConfirmStep />
        });

        _validationSchemas.push(Yup.object().shape({}));

        setSteps(_steps);
        setValidationSchemas(_validationSchemas);
    }, [installation, community]);

    useEffect(() => {
        setActiveStep(0);
    }, [open]);

    return (
        <DialogContent>
            <Divider fullWidth sx={{ mb: 2 }} />
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchemas[activeStep]}
                validateOnBlur={false}
                validateOnChange={false}
                enableReinitialize={true}
                onSubmit={handleSubmit}
            >
                {(formik) => (
                    <Form>
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {steps.map((step, index) => (
                                <Step key={index}>
                                    <StepLabel>{step.label}</StepLabel>
                                    <StepContent>
                                        <Box maxWidth="lg">
                                            <Stack spacing={4} direction="column">

                                                {step.component}
                                                <Stack spacing={2} direction="row">
                                                    {activeStep > 0 && <IndieBackButton id="back_button" disabled={submitting} onClick={() => setActiveStep((prevActiveStep) => prevActiveStep - 1)} />}
                                                    {activeStep < steps.length - 1 && (
                                                        <IndieNextStepButton id="next_step_button" type="submit" variant="contained" />
                                                    )}
                                                    {activeStep === steps.length - 1 && (
                                                        <IndieSaveButton id="save_button" type="submit" variant="contained" disabled={submitting} loading={submitting} />
                                                    )}
                                                </Stack>

                                            </Stack>
                                        </Box>
                                    </StepContent>
                                </Step>
                            ))}
                        </Stepper>
                    </Form>
                )}
            </Formik>
        </DialogContent>
    );
}

const SelectInstallationStep = ({ initialInstallation = null }) => {

    const { getCommunityInstallations } = useContext(CommunityContext);
    const [selectedInstallation, setSelectedInstallation] = useState(null);

    const { setFieldValue, errors } = useFormikContext();
    const { t } = useTranslation();

    const columns = useMemo(() => [
        {
            accessor: 'physical_unit.consumption_deliverypoint',
            Header: t("label.table.header.consumptionDeliverypoint")
        },
        {
            accessor: 'physical_unit.production_deliverypoint',
            Header: t("label.table.header.productionDeliverypoint")
        },
        {
            accessor: 'physical_unit.zip_code',
            Header: t("label.table.header.zipCode")
        },
        {
            accessor: 'physical_unit.address',
            Header: t("label.table.header.address")
        },
    ], []);

    const handleRowClick = (row) => {
        setSelectedInstallation(row);
        setFieldValue("installation_id", row.id);
        setFieldValue("installation", row);
    };

    return (
        <Stack spacing={2}>
            {initialInstallation ? (
                <Alert width={"md"} severity="success" variant="outlined">
                    <Stack spacing={0.5}>
                        <Typography variant="body2">{t("newTarriffPlan.initialInstallationData")}</Typography>
                        <IndieLabelAndValue label={t("label.form.dgegid")} value={initialInstallation.ext_member_id} />
                        <IndieLabelAndValue label={t("label.form.consumptionDeliveryPoint")} value={initialInstallation.physical_unit?.consumption_deliverypoint} />
                        <IndieLabelAndValue label={t("label.form.productionDeliveryPoint")} value={initialInstallation.physical_unit?.production_deliverypoint} />
                    </Stack>
                </Alert>
            ) : (
                <ApplicationList
                    id="community_installations_table"
                    dataFetcher={getCommunityInstallations}
                    columns={columns}
                    rowOnClick2={handleRowClick}
                    outlined
                    selectable
                    showAppBar
                />
            )}
            {errors.installation_id && <Typography id="form_error_text_installation_id" variant="caption" color="error">{errors.installation_id}</Typography>}
        </Stack>
    );
}

const ConfirmStep = () => {
    const { t } = useTranslation();
    const formik = useFormikContext();
    const { community } = useContext(CommunityContext);

    const installation = formik.values.installation;
    const client = formik.values.client;

    return (
        <Stack spacing={2}>
            <Typography variant='subtitle2' color='textSecondary'>{t("label.step.confirm.subtitle")}</Typography>
            <Stack spacing={1}>
                <Typography variant='subtitle2' color='textSecondary'>{t("label.form.community")}</Typography>
                <Divider />
                <IndieLabelAndValue label={t("label.form.community")} value={community?.name} />
            </Stack>
            <Stack spacing={1}>
                <Typography variant='subtitle2' color='textSecondary'>{t("label.step.selectInstallation")}</Typography>
                <Divider />
                <IndieLabelAndValue label={t("label.form.consumptionDeliveryPoint")} value={installation?.physical_unit.consumption_deliverypoint} />
                <IndieLabelAndValue label={t("label.form.productionDeliveryPoint")} value={installation?.physical_unit.production_deliverypoint} />
                <IndieLabelAndValue label={t("label.form.zipCode")} value={installation?.physical_unit.zip_code} />
                <IndieLabelAndValue label={t("label.form.address")} value={installation?.physical_unit.address} />
            </Stack>
            <Stack spacing={1}>
                <Typography variant='subtitle2' color='textSecondary'>{t("label.form.client")}</Typography>
                <Divider />
                <IndieLabelAndValue label={t("label.form.name")} value={client?.name} />
                <IndieLabelAndValue label={t("label.form.fiscalNumber")} value={client?.fiscal_number} />
                <IndieLabelAndValue label={t("label.form.email")} value={client?.email} />
                <IndieLabelAndValue label={t("label.form.phone")} value={client?.phone} />
            </Stack>
        </Stack>
    );
}

export default ConfigureCommunityMemberDialogContent;
